import PropTypes from 'prop-types';
import { globalizeSelectors } from '../Utils';
import type {
  Standalone2CloudAction,
  Standalone2CloudConnectionParameters,
  Standalone2CloudOrderStatusPayload,
  Standalone2CloudProgressPayload,
  Standalone2CloudSite,
  Standalone2CloudSiteParameters,
  Standalone2CloudState,
  Standalone2CloudSyncFinishedPayload,
  Standalone2CloudSyncPayload,
} from '../types/Standalone2Cloud';

const types = {
  LIST_DATABASES: 'STANDALONE2CLOUD/LIST_DATABASES',
  LIST_DATABASES_SUCCESS: 'STANDALONE2CLOUD/LIST_DATABASES_SUCCESS',
  LIST_DATABASES_FAILURE: 'STANDALONE2CLOUD/LIST_DATABASES_FAILURE',
  LIST_SITES: 'STANDALONE2CLOUD/LIST_SITES',
  LIST_SITES_SUCCESS: 'STANDALONE2CLOUD/LIST_SITES_SUCCESS',
  LIST_SITES_FAILURE: 'STANDALONE2CLOUD/LIST_SITES_FAILURE',
  START_SYNC: 'STANDALONE2CLOUD/START_SYNC',
  START_SYNC_SUCCESS: 'STANDALONE2CLOUD/START_SYNC_SUCCESS',
  START_SYNC_FAILURE: 'STANDALONE2CLOUD/START_SYNC_FAILURE',
  CANCEL_SYNC: 'STANDALONE2CLOUD/CANCEL_SYNC',
  PROGRESS_UPDATED: 'STANDALONE2CLOUD/PROGRESS_UPDATED',
  SYNC_STARTED: 'STANDALONE2CLOUD/SYNC_STARTED',
  SYNC_FINISHED: 'STANDALONE2CLOUD/SYNC_FINISHED',
  SYNC_CANCELLED: 'STANDALONE2CLOUD/SYNC_CANCELLED',
  ORDER_STATUS_UPDATED: 'STANDALONE2CLOUD/ORDER_STATUS_UPDATED',
  ORDER_STATUS_FAILURE: 'STANDALONE2CLOUD/ORDER_STATUS_FAILURE',
  POLL_ORDER_STATUS_START: 'STANDALONE2CLOUD/POLL_ORDER_STATUS_START',
  POLL_ORDER_STATUS_STOP: 'STANDALONE2CLOUD/POLL_ORDER_STATUS_STOP',
} as const;

const mountPath = 'standalone2cloud';

const initialState: Standalone2CloudState = {
  databases: [],
  sites: [],
  isFetchingDatabases: false,
  isFetchingSites: false,
  isSyncRunning: false,
  progressMessage: null,
  progressPercent: 0,
  error: null,
  resultMessage: null,
  orderSyncStatus: 'idle',
  orderSyncPercentage: 0,
  orderSyncStats: {},
  orderSyncError: null,
  orderSyncTaskId: null,
};

const actions = {
  listDatabases: (
    payload: Standalone2CloudConnectionParameters
  ): Standalone2CloudAction<Standalone2CloudConnectionParameters> => ({
    type: types.LIST_DATABASES,
    payload,
  }),
  listDatabasesSuccess: (
    databases: string[]
  ): Standalone2CloudAction<string[]> => ({
    type: types.LIST_DATABASES_SUCCESS,
    payload: databases,
  }),
  listDatabasesFailure: (error: unknown): Standalone2CloudAction<unknown> => ({
    type: types.LIST_DATABASES_FAILURE,
    payload: error,
  }),
  listSites: (
    payload: Standalone2CloudSiteParameters
  ): Standalone2CloudAction<Standalone2CloudSiteParameters> => ({
    type: types.LIST_SITES,
    payload,
  }),
  listSitesSuccess: (
    sites: Standalone2CloudSite[]
  ): Standalone2CloudAction<Standalone2CloudSite[]> => ({
    type: types.LIST_SITES_SUCCESS,
    payload: sites,
  }),
  listSitesFailure: (error: unknown): Standalone2CloudAction<unknown> => ({
    type: types.LIST_SITES_FAILURE,
    payload: error,
  }),
  startSync: (
    payload: Standalone2CloudSyncPayload
  ): Standalone2CloudAction<Standalone2CloudSyncPayload> => ({
    type: types.START_SYNC,
    payload,
  }),
  startSyncSuccess: (): Standalone2CloudAction => ({
    type: types.START_SYNC_SUCCESS,
  }),
  startSyncFailure: (error: unknown): Standalone2CloudAction<unknown> => ({
    type: types.START_SYNC_FAILURE,
    payload: error,
  }),
  cancelSync: (): Standalone2CloudAction => ({ type: types.CANCEL_SYNC }),
  progressUpdated: (
    message: string,
    percent: number
  ): Standalone2CloudAction<Standalone2CloudProgressPayload> => ({
    type: types.PROGRESS_UPDATED,
    payload: { message, percent },
  }),
  syncStarted: (): Standalone2CloudAction => ({ type: types.SYNC_STARTED }),
  syncFinished: (
    success: boolean,
    message: string
  ): Standalone2CloudAction<Standalone2CloudSyncFinishedPayload> => ({
    type: types.SYNC_FINISHED,
    payload: { success, message },
  }),
  syncCancelled: (): Standalone2CloudAction => ({
    type: types.SYNC_CANCELLED,
  }),
  orderStatusUpdated: (
    status: Standalone2CloudOrderStatusPayload
  ): Standalone2CloudAction<Standalone2CloudOrderStatusPayload> => ({
    type: types.ORDER_STATUS_UPDATED,
    payload: status,
  }),
  orderStatusFailure: (error: unknown): Standalone2CloudAction<unknown> => ({
    type: types.ORDER_STATUS_FAILURE,
    payload: error,
  }),
  pollOrderStatusStart: (
    siteid: number | string
  ): Standalone2CloudAction<number | string> => ({
    type: types.POLL_ORDER_STATUS_START,
    payload: siteid,
  }),
  pollOrderStatusStop: (): Standalone2CloudAction => ({
    type: types.POLL_ORDER_STATUS_STOP,
  }),
};

function payloadAs<Payload>(action: Standalone2CloudAction): Payload {
  return action.payload as Payload;
}

function reducer(
  state: Standalone2CloudState = initialState,
  action: Standalone2CloudAction
): Standalone2CloudState {
  switch (action.type) {
    case types.LIST_DATABASES:
      return {
        ...state,
        isFetchingDatabases: true,
        error: null,
        databases: [],
      };
    case types.LIST_DATABASES_SUCCESS:
      return {
        ...state,
        isFetchingDatabases: false,
        databases: payloadAs<string[]>(action),
      };
    case types.LIST_DATABASES_FAILURE:
      return {
        ...state,
        isFetchingDatabases: false,
        error: action.payload,
      };
    case types.LIST_SITES:
      return {
        ...state,
        isFetchingSites: true,
        error: null,
        sites: [],
      };
    case types.LIST_SITES_SUCCESS:
      return {
        ...state,
        isFetchingSites: false,
        sites: payloadAs<Standalone2CloudSite[]>(action),
      };
    case types.LIST_SITES_FAILURE:
      return {
        ...state,
        isFetchingSites: false,
        error: action.payload,
      };
    case types.SYNC_STARTED:
      return {
        ...state,
        isSyncRunning: true,
        progressMessage: null,
        progressPercent: 0,
        resultMessage: null,
        error: null,
      };
    case types.PROGRESS_UPDATED: {
      const progress = payloadAs<Standalone2CloudProgressPayload>(action);
      return {
        ...state,
        progressMessage: progress.message,
        progressPercent: progress.percent,
      };
    }
    case types.SYNC_FINISHED: {
      const syncResult = payloadAs<Standalone2CloudSyncFinishedPayload>(action);
      return {
        ...state,
        isSyncRunning: false,
        resultMessage: syncResult.message,
        progressPercent: syncResult.success ? 100 : state.progressPercent,
      };
    }
    case types.SYNC_CANCELLED:
      return {
        ...state,
        isSyncRunning: false,
        resultMessage: 'Cancelled',
        progressMessage: 'Cancelled',
      };
    case types.START_SYNC_FAILURE:
      return {
        ...state,
        isSyncRunning: false,
        error: action.payload,
      };
    case types.START_SYNC_SUCCESS:
      return {
        ...state,
        error: null,
      };
    case types.ORDER_STATUS_UPDATED: {
      const payload =
        payloadAs<Standalone2CloudOrderStatusPayload>(action) || {};
      const status = payload.status || 'idle';
      return {
        ...state,
        orderSyncStatus: status,
        orderSyncPercentage:
          typeof payload.percentage === 'number' &&
          Number.isFinite(payload.percentage)
            ? payload.percentage
            : 0,
        orderSyncStats: payload.stats || {},
        orderSyncError: payload.error || null,
        orderSyncTaskId: payload.task_id || null,
      };
    }
    case types.ORDER_STATUS_FAILURE:
      return {
        ...state,
        orderSyncError: action.payload,
      };
    default:
      return state;
  }
}

const localSelectors = {
  databases: (state: Standalone2CloudState): string[] => state.databases,
  sites: (state: Standalone2CloudState): Standalone2CloudSite[] => state.sites,
  isFetchingDatabases: (state: Standalone2CloudState): boolean =>
    state.isFetchingDatabases,
  isFetchingSites: (state: Standalone2CloudState): boolean =>
    state.isFetchingSites,
  isSyncRunning: (state: Standalone2CloudState): boolean => state.isSyncRunning,
  progressMessage: (
    state: Standalone2CloudState
  ): Standalone2CloudState['progressMessage'] => state.progressMessage,
  progressPercent: (state: Standalone2CloudState): number =>
    state.progressPercent,
  error: (state: Standalone2CloudState): Standalone2CloudState['error'] =>
    state.error,
  resultMessage: (
    state: Standalone2CloudState
  ): Standalone2CloudState['resultMessage'] => state.resultMessage,
  orderSyncStatus: (state: Standalone2CloudState): string =>
    state.orderSyncStatus,
  orderSyncPercentage: (state: Standalone2CloudState): number =>
    state.orderSyncPercentage,
  orderSyncStats: (
    state: Standalone2CloudState
  ): Standalone2CloudState['orderSyncStats'] => state.orderSyncStats,
  orderSyncError: (
    state: Standalone2CloudState
  ): Standalone2CloudState['orderSyncError'] => state.orderSyncError,
  orderSyncTaskId: (
    state: Standalone2CloudState
  ): Standalone2CloudState['orderSyncTaskId'] => state.orderSyncTaskId,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

const propType = PropTypes.shape({
  databases: PropTypes.arrayOf(PropTypes.string),
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      siteid: PropTypes.number,
      sitename: PropTypes.string,
    })
  ),
  isFetchingDatabases: PropTypes.bool,
  isFetchingSites: PropTypes.bool,
  isSyncRunning: PropTypes.bool,
  progressMessage: PropTypes.string,
  progressPercent: PropTypes.number,
  error: PropTypes.string,
  resultMessage: PropTypes.string,
});

export {
  types as actionTypes,
  actions as default,
  selectors,
  reducer,
  propType,
};
